export type FontsType = {
  base: string;
  baseMedium: string;
  baseBold: string;
  bold: string;
  customBold: string;
  emphasis: string;
};

export type FontsStyle = {
  fontFamily?: string;
  fontSize?: number;
  lineHeight?: number;
  color?: string;
  textDecoration?: string;
  fontWeight?:
    | 'bold'
    | 'normal'
    | '100'
    | '200'
    | '300'
    | '400'
    | '500'
    | '600'
    | '700'
    | '800'
    | '900';
  opacity?: number;
  fontStyle?: 'normal' | 'italic';
};

export type StyleType = Record<string, FontsStyle>;

export const typeByLabel: Record<string, FontsType> = {
  PRIMARY: {
    base: 'Poppins',
    baseMedium: 'Poppins',
    baseBold: 'Poppins',
    bold: 'Work Sans',
    customBold: 'Ubuntu',
    emphasis: 'Work Sans',
  },
  SECONDARY: {
    base: 'Poppins',
    baseMedium: 'Poppins',
    baseBold: 'Poppins',
    bold: 'Poppins',
    customBold: 'Poppins-bold',
    emphasis: 'Work Sans',
  },
};

export const size = {
  h1: 38,
  h2: 34,
  h3: 28,
  h4: 24,
  h5: 20,
  h6: 19,
  input: 18,
  regular: 17,
  medium: 14,
  small: 12,
  tiny: 11,
};

export const styleByLabel: Record<string, (type: FontsType) => StyleType> = {
  PRIMARY: type => ({
    // Headings
    heading1: {
      fontFamily: type.baseBold,
      fontWeight: '700',
      fontSize: 26,
      lineHeight: 64,
    },
    heading2: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 48,
      lineHeight: 56,
    },
    heading3: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 40,
      lineHeight: 48,
    },
    heading4: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 32,
      lineHeight: 40,
    },
    heading5: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 24,
      lineHeight: 32,
    },
    heading6: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 20,
      lineHeight: 24,
    },
    heading7: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 16,
      lineHeight: 20,
    },
    heading8: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 7,
      lineHeight: 8,
    },
    // Body Regular
    bodyRegularXL: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 20,
      lineHeight: 28,
    },
    bodyRegularLG: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 18,
      lineHeight: 24,
    },
    bodyRegularMD: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 16,
      lineHeight: 20,
    },
    bodyRegularSM: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 14,
      lineHeight: 16,
    },
    bodyRegularXS: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 12,
      lineHeight: 16,
    },
    bodyRegularCap: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 11,
      lineHeight: 14,
    },
    // Body Bold
    bodyBoldXL: {
      fontFamily: type.baseBold,
      fontWeight: '700',
      fontSize: 20,
      lineHeight: 28,
    },
    bodyBoldLG: {
      fontFamily: type.baseBold,
      fontWeight: '700',
      fontSize: 18,
      lineHeight: 24,
    },
    bodyBoldMD: {
      fontFamily: type.baseBold,
      fontWeight: '700',
      fontSize: 16,
      lineHeight: 20,
    },
    bodyBoldSM: {
      fontFamily: type.baseBold,
      fontWeight: '700',
      fontSize: 14,
      lineHeight: 16,
    },
    bodyBoldXS: {
      fontFamily: type.baseBold,
      fontWeight: '700',
      fontSize: 12,
      lineHeight: 16,
    },
    bodyBoldCap: {
      fontFamily: type.baseBold,
      fontWeight: '700',
      fontSize: 11,
      lineHeight: 14,
    },
    // Body Underline
    bodyUnderlineXL: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 20,
      lineHeight: 28,
    },
    bodyUnderlineLG: {
      fontFamily: type.base,
      textDecoration: 'underline',
      fontWeight: '400',
      fontSize: 18,
      lineHeight: 24,
    },
    bodyUnderlineMD: {
      fontFamily: type.base,
      textDecoration: 'underline',
      fontWeight: '400',
      fontSize: 16,
      lineHeight: 20,
    },
    bodyUnderlineSM: {
      fontFamily: type.base,
      textDecoration: 'underline',
      fontWeight: '400',
      fontSize: 14,
      lineHeight: 16,
    },
    bodyUnderlineXS: {
      fontFamily: type.base,
      textDecoration: 'underline',
      fontWeight: '400',
      fontSize: 12,
      lineHeight: 16,
    },
    bodyUnderlineCap: {
      fontFamily: type.base,
      textDecoration: 'underline',
      fontWeight: '400',
      fontSize: 11,
      lineHeight: 14,
    },
    // Button Types
    buttonLG: {
      fontFamily: type.baseMedium,
      fontWeight: '500',
      fontSize: 20,
      lineHeight: 24,
    },
    buttonMD: {
      fontFamily: type.baseMedium,
      fontWeight: '500',
      fontSize: 16,
      lineHeight: 20,
    },
    buttonSM: {
      fontFamily: type.baseMedium,
      fontWeight: '500',
      fontSize: 14,
      lineHeight: 16,
    },
    // Indicators
    indicatorXLG: {
      fontFamily: type.base,
      fontSize: 12,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 14,
    },
    indicatorLG: {
      fontFamily: type.base,
      fontSize: 8,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 10,
    },
    indicatorMD: {
      fontFamily: type.base,
      fontSize: 7,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 9,
    },
    indicatorSM: {
      fontFamily: type.base,
      fontSize: 5,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 6,
    },
    indicatorXS: {
      fontFamily: type.base,
      fontSize: 4,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 5,
    },
    indicator2XS: {
      fontFamily: type.base,
      fontSize: 3,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 4,
    },
    indicator3XS: {
      fontFamily: type.base,
      fontSize: 2,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 3,
    },
  }),

  SECONDARY: type => ({
    // Headings
    heading1: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 56,
      lineHeight: 64,
    },
    heading2: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 48,
      lineHeight: 56,
    },
    heading3: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 40,
      lineHeight: 48,
    },
    heading4: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 32,
      lineHeight: 40,
    },
    heading5: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 24,
      lineHeight: 32,
    },
    heading6: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 20,
      lineHeight: 24,
    },
    heading7: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 16,
      lineHeight: 20,
    },
    heading8: {
      fontFamily: type.customBold,
      fontWeight: '700',
      fontSize: 7,
      lineHeight: 8,
    },
    // Body Regular
    bodyRegularXL: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 20,
      lineHeight: 28,
    },
    bodyRegularLG: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 18,
      lineHeight: 24,
    },
    bodyRegularMD: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 16,
      lineHeight: 20,
    },
    bodyRegularSM: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 14,
      lineHeight: 16,
    },
    bodyRegularXS: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 12,
      lineHeight: 16,
    },
    bodyRegularCap: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 11,
      lineHeight: 14,
    },
    // Body Bold
    bodyBoldXL: {
      fontFamily: type.baseBold,
      fontWeight: '700',
      fontSize: 20,
      lineHeight: 28,
    },
    bodyBoldLG: {
      fontFamily: type.baseBold,
      fontWeight: '700',
      fontSize: 18,
      lineHeight: 24,
    },
    bodyBoldMD: {
      fontFamily: type.baseBold,
      fontWeight: '700',
      fontSize: 16,
      lineHeight: 20,
    },
    bodyBoldSM: {
      fontFamily: type.baseBold,
      fontWeight: '700',
      fontSize: 14,
      lineHeight: 16,
    },
    bodyBoldXS: {
      fontFamily: type.baseBold,
      fontWeight: '700',
      fontSize: 12,
      lineHeight: 16,
    },
    bodyBoldCap: {
      fontFamily: type.baseBold,
      fontWeight: '700',
      fontSize: 11,
      lineHeight: 14,
    },
    // Body Underline
    bodyUnderlineXL: {
      fontFamily: type.base,
      fontWeight: '400',
      fontSize: 20,
      lineHeight: 28,
    },
    bodyUnderlineLG: {
      fontFamily: type.base,
      textDecoration: 'underline',
      fontWeight: '400',
      fontSize: 18,
      lineHeight: 24,
    },
    bodyUnderlineMD: {
      fontFamily: type.base,
      textDecoration: 'underline',
      fontWeight: '400',
      fontSize: 16,
      lineHeight: 20,
    },
    bodyUnderlineSM: {
      fontFamily: type.base,
      textDecoration: 'underline',
      fontWeight: '400',
      fontSize: 14,
      lineHeight: 16,
    },
    bodyUnderlineXS: {
      fontFamily: type.base,
      textDecoration: 'underline',
      fontWeight: '400',
      fontSize: 12,
      lineHeight: 16,
    },
    bodyUnderlineCap: {
      fontFamily: type.base,
      textDecoration: 'underline',
      fontWeight: '400',
      fontSize: 11,
      lineHeight: 14,
    },
    // Button Types
    buttonLG: {
      fontFamily: type.baseMedium,
      fontWeight: '500',
      fontSize: 20,
      lineHeight: 24,
    },
    buttonMD: {
      fontFamily: type.baseMedium,
      fontWeight: '500',
      fontSize: 16,
      lineHeight: 20,
    },
    buttonSM: {
      fontFamily: type.baseMedium,
      fontWeight: '500',
      fontSize: 14,
      lineHeight: 16,
    },
    // Indicators
    indicatorXLG: {
      fontFamily: type.base,
      fontSize: 12,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 14,
    },
    indicatorLG: {
      fontFamily: type.base,
      fontSize: 8,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 10,
    },
    indicatorMD: {
      fontFamily: type.base,
      fontSize: 7,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 9,
    },
    indicatorSM: {
      fontFamily: type.base,
      fontSize: 5,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 6,
    },
    indicatorXS: {
      fontFamily: type.base,
      fontSize: 4,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 5,
    },
    indicator2XS: {
      fontFamily: type.base,
      fontSize: 3,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 4,
    },
    indicator3XS: {
      fontFamily: type.base,
      fontSize: 2,
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: 3,
    },
  }),
};
